import React, { createContext, useContext } from 'react';

/**
 * Demo Mode Context
 *
 * When REACT_APP_DEMO_MODE=true, the entire app runs with mock data
 * and no Firebase authentication or database calls are made.
 */

const DemoContext = createContext({ isDemo: false });

export const useDemo = () => useContext(DemoContext);

// ─── Mock Data ─────────────────────────────────────────────

const NOW = new Date();
const daysAgo = (n) => new Date(NOW.getTime() - n * 86400000);

export const DEMO_USER = {
  uid: 'demo-user-001',
  email: 'dr.smith@smileanalyst.xyz',
  displayName: 'Dr. Sarah Smith'
};

export const DEMO_PROFILE = {
  id: 'demo-user-001',
  email: 'dr.smith@smileanalyst.xyz',
  displayName: 'Dr. Sarah Smith',
  role: 'admin',
  practiceId: 'demo-practice-001',
  createdAt: daysAgo(90),
  lastLogin: NOW
};

export const DEMO_PATIENTS = [
  {
    id: 'patient-001',
    firstName: 'Emily',
    lastName: 'Johnson',
    email: 'emily.j@email.com',
    phone: '(555) 234-5678',
    dateOfBirth: '1992-03-15',
    gender: 'female',
    address: '123 Oak Street, Austin, TX 78701',
    emergencyContact: 'Mark Johnson - (555) 345-6789',
    notes: 'Interested in full smile makeover. Previous orthodontic treatment at age 14.',
    createdAt: daysAgo(45),
    updatedAt: daysAgo(2)
  },
  {
    id: 'patient-002',
    firstName: 'Michael',
    lastName: 'Chen',
    email: 'mchen@email.com',
    phone: '(555) 876-5432',
    dateOfBirth: '1985-07-22',
    gender: 'male',
    address: '456 Elm Ave, Austin, TX 78702',
    emergencyContact: 'Lisa Chen - (555) 987-6543',
    notes: 'Crown replacement on #8 and #9. Wants whiter shade.',
    createdAt: daysAgo(30),
    updatedAt: daysAgo(5)
  },
  {
    id: 'patient-003',
    firstName: 'Sophia',
    lastName: 'Martinez',
    email: 'sophia.m@email.com',
    phone: '(555) 111-2233',
    dateOfBirth: '1998-11-08',
    gender: 'female',
    address: '789 Pine Rd, Austin, TX 78703',
    emergencyContact: 'Carlos Martinez - (555) 222-3344',
    notes: 'Veneer consultation. Composite bonding on upper anteriors.',
    createdAt: daysAgo(14),
    updatedAt: daysAgo(1)
  },
  {
    id: 'patient-004',
    firstName: 'James',
    lastName: 'Williams',
    email: 'jwilliams@email.com',
    phone: '(555) 444-5566',
    dateOfBirth: '1978-01-30',
    gender: 'male',
    address: '321 Cedar Blvd, Austin, TX 78704',
    emergencyContact: 'Patricia Williams - (555) 555-6677',
    notes: 'Full mouth reconstruction. TMJ issues noted.',
    createdAt: daysAgo(60),
    updatedAt: daysAgo(10)
  },
  {
    id: 'patient-005',
    firstName: 'Olivia',
    lastName: 'Davis',
    email: 'odavis@email.com',
    phone: '(555) 777-8899',
    dateOfBirth: '2001-05-12',
    gender: 'female',
    address: '654 Maple Dr, Austin, TX 78705',
    emergencyContact: 'Robert Davis - (555) 888-9900',
    notes: 'Post-Invisalign refinement. Whitening + bonding planned.',
    createdAt: daysAgo(7),
    updatedAt: daysAgo(1)
  }
];

export const DEMO_CASES = {
  'patient-001': [
    {
      id: 'case-001',
      patientId: 'patient-001',
      name: 'Full Smile Makeover',
      description: 'Comprehensive esthetic rehabilitation including veneers on #4-13, crown lengthening, and whitening.',
      treatmentGoals: 'Achieve symmetrical smile line\nCorrect gingival display\nImprove tooth proportions to golden ratio\nShade upgrade to BL2',
      status: 'active',
      currentVersion: 2,
      createdAt: daysAgo(44),
      updatedAt: daysAgo(2)
    }
  ],
  'patient-002': [
    {
      id: 'case-002',
      patientId: 'patient-002',
      name: 'Anterior Crown Replacement',
      description: 'Replace aging PFM crowns on #8 and #9 with e.max lithium disilicate.',
      treatmentGoals: 'Natural translucency\nImproved marginal adaptation\nShade match to adjacent teeth',
      status: 'active',
      currentVersion: 1,
      createdAt: daysAgo(28),
      updatedAt: daysAgo(5)
    }
  ],
  'patient-003': [
    {
      id: 'case-003',
      patientId: 'patient-003',
      name: 'Veneer Consultation',
      description: 'Upper anterior composite bonding to close diastema and improve tooth shape.',
      treatmentGoals: 'Close midline diastema\nImprove lateral incisor shape\nMinimal prep approach',
      status: 'active',
      currentVersion: 1,
      createdAt: daysAgo(12),
      updatedAt: daysAgo(1)
    }
  ]
};

export const DEMO_PHASES = {
  'case-001': [
    { id: 'phase-001', phaseType: 'initial', status: 'completed', notes: 'Full records completed. Ready for treatment planning.', photos: ['p1','p2','p3','p4','p5','p6','p7','p8','p9'] },
    { id: 'phase-002', phaseType: 'postOrtho', status: 'in_progress', notes: 'Ortho finishing phase - 2 weeks remaining.', photos: ['p10','p11','p12'] },
    { id: 'phase-003', phaseType: 'provisional', status: 'not_started', notes: '', photos: [] },
    { id: 'phase-004', phaseType: 'final', status: 'not_started', notes: '', photos: [] }
  ],
  'case-002': [
    { id: 'phase-005', phaseType: 'initial', status: 'completed', notes: 'Impressions and photos taken.', photos: ['p20','p21','p22','p23','p24'] },
    { id: 'phase-006', phaseType: 'postOrtho', status: 'not_started', notes: '', photos: [] },
    { id: 'phase-007', phaseType: 'provisional', status: 'in_progress', notes: 'Provisional crowns placed. Evaluating esthetics.', photos: ['p25'] },
    { id: 'phase-008', phaseType: 'final', status: 'not_started', notes: '', photos: [] }
  ],
  'case-003': [
    { id: 'phase-009', phaseType: 'initial', status: 'in_progress', notes: 'Photos partially taken.', photos: ['p30','p31'] },
    { id: 'phase-010', phaseType: 'postOrtho', status: 'not_started', notes: '', photos: [] },
    { id: 'phase-011', phaseType: 'provisional', status: 'not_started', notes: '', photos: [] },
    { id: 'phase-012', phaseType: 'final', status: 'not_started', notes: '', photos: [] }
  ]
};

// Placeholder dental photo URLs (solid color placeholders since we can't use real patient photos)
const placeholderPhoto = (cat, idx) => ({
  id: `photo-${cat}-${idx}`,
  filename: `${cat}_${Date.now()}.jpg`,
  storagePath: `demo/${cat}.jpg`,
  downloadURL: `https://placehold.co/800x600/e2e8f0/64748b?text=${encodeURIComponent(cat.replace(/_/g, ' '))}`,
  category: cat,
  fileSize: 2400000,
  fileType: 'image/jpeg',
  uploadedBy: 'demo-user-001',
  uploadedAt: daysAgo(idx + 1)
});

export const DEMO_PHOTOS = {
  'phase-001': [
    placeholderPhoto('frontal_repose', 1),
    placeholderPhoto('frontal_smile', 2),
    placeholderPhoto('profile_repose', 3),
    placeholderPhoto('profile_smile', 4),
    placeholderPhoto('frontal_occlusion', 5),
    placeholderPhoto('right_buccal', 6),
    placeholderPhoto('left_buccal', 7),
    placeholderPhoto('maxillary_occlusal', 8),
    placeholderPhoto('mandibular_occlusal', 9),
  ],
  'phase-002': [
    placeholderPhoto('frontal_smile', 10),
    placeholderPhoto('frontal_occlusion', 11),
    placeholderPhoto('right_buccal', 12),
  ],
  'phase-005': [
    placeholderPhoto('frontal_smile', 20),
    placeholderPhoto('frontal_occlusion', 21),
    placeholderPhoto('right_buccal', 22),
    placeholderPhoto('left_buccal', 23),
    placeholderPhoto('upper_anterior', 24),
  ],
  'phase-007': [
    placeholderPhoto('frontal_smile', 25),
  ],
  'phase-009': [
    placeholderPhoto('frontal_repose', 30),
    placeholderPhoto('frontal_smile', 31),
  ]
};

export const DEMO_EXAMS = {
  'case-001': {
    history: {
      chiefComplaint: 'Patient presents with esthetic concerns regarding upper anterior teeth. Interested in full smile makeover. Reports occasional sensitivity on #14.',
      dentalHistory: 'Previous orthodontic treatment at age 14. Composite bonding on #8 and #9 five years ago. Regular 6-month cleanings.',
      estheticConcerns: 'HIGH',
      estheticNotes: 'Wants whiter, more symmetrical smile. Concerned about gum line asymmetry and tooth proportions.',
      notes: ''
    },
    cancer: {
      rows: {
        'Head': { status: 'pathology', description: 'sdfgsdfg' },
        'Neck': { status: 'pathology', description: 'dsfgds' },
        'Face': { status: 'pathology', description: '' },
        'Ears': { status: 'pathology', description: '' },
        'Eyes': { status: 'pathology', description: 'fasdfasdf' },
        'Lips': { status: 'pathology', description: '' },
        'Buccal Mucosa': { status: 'wnl', description: '' },
        'Tongue': { status: 'wnl', description: '' },
        'Floor Of The Mouth': { status: 'wnl', description: '' },
        'The Oropharynx': { status: 'wnl', description: '' },
        'Tonsils': { status: 'wnl', description: '' },
        'Hard & Soft Palate': { status: 'wnl', description: '' },
        'Posterior Pharyngeal Wall': { status: 'wnl', description: '' }
      }
    },
    tmjQuestions: {
      answers: {
        0: { answer: 'yes', description: 'sdfgdsfg' },
        1: { answer: 'yes', description: 'askjdf asd' },
        2: { answer: 'no', description: '' },
        3: { answer: 'no', description: '' },
        4: { answer: 'no', description: '' },
        5: { answer: 'no', description: '' },
        6: { answer: 'no', description: 'Jaw trauma' }
      }
    },
    muscles: {
      rightSide: true,
      leftSide: true,
      muscleData: {
        Right_Side: {
          'Temporalis (Anterior)': { status: 'wnl' },
          'Masseter (Body)': { status: 'pathology', description: 'Tenderness on palpation' }
        },
        Left_Side: {
          'Temporalis (Anterior)': { status: 'wnl' },
          'Lateral Pterygoid': { status: 'pathology', description: 'Moderate tenderness' }
        }
      }
    },
    tmjExam1: {
      symptomStatus: 'SYMPTOMATIC',
      maxOpening: '42',
      protrusion: '7',
      rtLateral: '9',
      ltLateral: '6',
      deviation: 'NONE',
      deflection: 'RIGHT'
    },
    tmjExam2: {
      rightTMJ: 'TENDERNESS',
      leftTMJ: 'TENSION',
      slideAnterior: true
    },
    joints: {
      rightDiagnosis: 'MPLOCK',
      leftDiagnosis: 'MPCLICK',
      rightNoise: 'CLICK',
      leftNoise: 'CLICK'
    },
    occlusalExam: {
      crowding: 'MILD',
      wear: 'SEVERE',
      mobility: 'NONE',
      fremitus: 'NONE'
    },
    occlusalStability: {
      instability: true,
      dawsonClass: 'IIA',
      angleClass: 'III',
      parafunction: 'BRUX'
    },
    airway: {
      severeObstruction: true,
      mouthBreathing: true
    },
    stopBang: {
      answers: {
        0: { answer: 'no' },
        1: { answer: 'no' },
        2: { answer: 'no' },
        3: { answer: 'no' },
        4: { answer: 'no' },
        5: { answer: 'no' },
        6: { answer: 'no' },
        7: { answer: 'yes' }
      }
    },
    mallampati: {
      score: 'CLASS 3'
    }
  }
};

export const DEMO_AUDIT_LOGS = [
  { id: 'log-1', action: 'login', resourceType: 'user', resourceId: 'demo-user-001', userId: 'demo-user-001', timestamp: daysAgo(0), userAgent: 'Chrome/120', details: {} },
  { id: 'log-2', action: 'read', resourceType: 'patient', resourceId: 'patient-001', userId: 'demo-user-001', timestamp: daysAgo(0), userAgent: 'Chrome/120', details: {} },
  { id: 'log-3', action: 'upload', resourceType: 'photo', resourceId: 'photo-1', userId: 'demo-user-001', timestamp: daysAgo(1), userAgent: 'Chrome/120', details: { category: 'frontal_smile' } },
  { id: 'log-4', action: 'create', resourceType: 'case', resourceId: 'case-003', userId: 'demo-user-001', timestamp: daysAgo(1), userAgent: 'Chrome/120', details: {} },
  { id: 'log-5', action: 'update', resourceType: 'patient', resourceId: 'patient-003', userId: 'demo-user-001', timestamp: daysAgo(2), userAgent: 'Chrome/120', details: {} },
  { id: 'log-6', action: 'read', resourceType: 'case', resourceId: 'case-001', userId: 'demo-user-001', timestamp: daysAgo(2), userAgent: 'Chrome/120', details: {} },
  { id: 'log-7', action: 'upload', resourceType: 'photo', resourceId: 'photo-2', userId: 'demo-user-001', timestamp: daysAgo(3), userAgent: 'Chrome/120', details: { category: 'frontal_occlusion' } },
  { id: 'log-8', action: 'create', resourceType: 'patient', resourceId: 'patient-005', userId: 'demo-user-001', timestamp: daysAgo(7), userAgent: 'Chrome/120', details: {} },
  { id: 'log-9', action: 'login', resourceType: 'user', resourceId: 'demo-user-001', userId: 'demo-user-001', timestamp: daysAgo(7), userAgent: 'Chrome/120', details: {} },
  { id: 'log-10', action: 'create', resourceType: 'case', resourceId: 'case-002', userId: 'demo-user-001', timestamp: daysAgo(28), userAgent: 'Chrome/120', details: {} },
];

export const DEMO_USERS = [
  { id: 'demo-user-001', displayName: 'Dr. Sarah Smith', email: 'dr.smith@smileanalyst.xyz', role: 'admin', createdAt: daysAgo(90), lastLogin: NOW },
  { id: 'demo-user-002', displayName: 'Dr. James Park', email: 'dr.park@smileanalyst.xyz', role: 'doctor', createdAt: daysAgo(60), lastLogin: daysAgo(1) },
  { id: 'demo-user-003', displayName: 'Maria Garcia', email: 'maria@smileanalyst.xyz', role: 'staff', createdAt: daysAgo(30), lastLogin: daysAgo(2) },
];

// ─── Smile Designs ─────────────────────────────────────────

export const DEMO_SMILE_DESIGNS = {
  'case-001': [
    {
      id: 'design-001',
      name: 'Golden Ratio Design',
      mode: 'overlay',
      sourcePhoto: {
        phaseId: 'phase-001',
        photoId: 'photo-frontal_smile-2',
        downloadURL: 'https://placehold.co/800x600/e2e8f0/64748b?text=frontal%20smile',
        category: 'frontal_smile',
      },
      canvasJSON: '{"version":"6.0.0","objects":[]}',
      referencePoints: {
        midline: { x: 400, y: 200 },
        leftCommissure: { x: 250, y: 350 },
        rightCommissure: { x: 550, y: 350 },
        incisalEdge: { x: 400, y: 340 },
      },
      designParams: {
        preset: 'golden',
        toothColor: '#F5F0E8',
        toothOpacity: 0.85,
        teethPerSide: 5,
        templateId: null,
      },
      thumbnailDataURL: '',
      createdBy: 'demo-user-001',
      createdAt: daysAgo(2),
      updatedBy: 'demo-user-001',
      updatedAt: daysAgo(1),
    }
  ],
};

// ─── Provider ──────────────────────────────────────────────

export const DemoProvider = ({ children }) => {
  return (
    <DemoContext.Provider value={{ isDemo: true }}>
      {children}
    </DemoContext.Provider>
  );
};

export default DemoContext;
